import { Box, Flex, Grid, Spinner, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paycheck } from 'assets/images';
import Button from 'components/Button';
import { FormikSelect } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { routePaths } from 'global/routePaths';
import { useDisconnectPaychexApi } from 'hooks/integrations/paychexApi/useDisconnectPaychexApi';
import { useFetchPaychexApiPaySettings } from 'hooks/integrations/paychexApi/useFetchPaychexApiPaySettings';
import {
  useSavePaychexApiPaySettings,
  IPaychexApiSettingsData,
} from 'hooks/integrations/paychexApi/useSavePaycheckApiPaySettings';
import { useCompanyInfo } from 'store/useUserStore';

import { initialPaychexApiSettingsData } from './schema';

const PaychexApiSettings: React.FC<unknown> = () => {
  const [formData, setFormData] = useState<any>(initialPaychexApiSettingsData);

  const { t } = useTranslation(['common', 'integrations', 'jobsTasks']);

  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();

  const { status: disconnectPaychexApiStatus, mutate: qbdDisconnectMutate } = useDisconnectPaychexApi();
  const {
    data: paychexPaySettingsData,
    status: fetchPaySettingsStatus,
    refetch: refetchPaychexApiPaySettings,
  } = useFetchPaychexApiPaySettings();
  const { status: savePaychexApiPaySettingsStatus, mutateAsync: paychexApiPaySettingsMutateAsync } =
    useSavePaychexApiPaySettings();

  useEffect(() => {
    if (fetchPaySettingsStatus === 'success') {
      const formData = { ...initialPaychexApiSettingsData };

      if (
        paychexPaySettingsData?.payload &&
        paychexPaySettingsData?.payload instanceof Array &&
        paychexPaySettingsData?.payload.length
      ) {
        paychexPaySettingsData?.payload.forEach((payItem: any) => {
          switch (payItem?.payroll_type) {
            case 'regular':
              formData.regular = payItem;
              break;

            case 'overtime':
              formData.overtime = payItem;
              break;

            case 'double_overtime':
              formData.double_overtime = payItem;
              break;

            case 'mileage':
              formData.mileage = payItem;
              break;

            default:
          }
        });
      }
      setFormData(formData);
    }
  }, [fetchPaySettingsStatus, paychexPaySettingsData]);

  const handleDisconnectPaychexApi = () => {
    qbdDisconnectMutate();
  };

  const onConfigureMapping = () => {
    navigate(routePaths.integrations.paychexApi.mapping);
  };

  const handleFormSubmit = async (values: typeof initialPaychexApiSettingsData) => {
    const postData: IPaychexApiSettingsData = {
      ...values,
      regular: values.regular?.id || '',
      overtime: values.overtime?.id || '',
      double_overtime: values.double_overtime?.id || '',
      mileage: values.mileage?.id || '',
    };
    paychexApiPaySettingsMutateAsync(postData).then(() => {
      refetchPaychexApiPaySettings();
    });
  };

  const { data: paychexApiSettingsData } = useFetchPaychexApiPaySettings();

  if (fetchPaySettingsStatus === 'loading') return <LogoLoader />;

  return (
    <Box>
      <Box maxWidth="200px" mb={4}>
        <img src={paycheck} alt="Paychex Logo" />
      </Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Flex flexDirection="column">
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px" mb={6}>
            <Text fontSize="md" mb={4}>
              {t('integrations:paychexApi.myPaychexApiFile')}
            </Text>

            <Text fontSize="sm" color="gray.500" mb={4}>
              {companyInfo?.name}
            </Text>

            <Text mb={4} color="primary.300" cursor="pointer" onClick={onConfigureMapping}>
              {t('integrations:quickbooks.configureMappings')}
            </Text>

            <Text color="primary.300" onClick={handleDisconnectPaychexApi} cursor="pointer">
              {t('integrations:paychexApi.disconnectFromPaychexApi')}{' '}
              {disconnectPaychexApiStatus === 'loading' && <Spinner size="xs" />}
            </Text>
          </Box>
        </Flex>
        <Box>
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
            <Text fontSize="md" mb={4}>
              {t('integrations:quickbooks.settings')}
            </Text>
            <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
              {() => (
                <Form noValidate>
                  <Box>
                    <FormikSelect
                      name="regular"
                      label={t('integrations:defaultRegularHoursPayrollItem')}
                      options={paychexApiSettingsData?.payload || []}
                      selectProps={{
                        placeholder: t('common:select'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="overtime"
                      label={t('integrations:defaultOvertimeHoursPayrollItem')}
                      options={paychexApiSettingsData?.payload || []}
                      selectProps={{
                        placeholder: t('common:select'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="double_overtime"
                      label={t('integrations:defaultDoubletimeHoursPayrollItem')}
                      options={paychexApiSettingsData?.payload || []}
                      selectProps={{
                        placeholder: t('common:select'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="mileage"
                      label={t('common:mileage')}
                      options={paychexApiSettingsData?.payload || []}
                      selectProps={{
                        placeholder: t('common:select'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Button
                    type="submit"
                    colorScheme="secondary"
                    isLoading={savePaychexApiPaySettingsStatus === 'loading'}
                    minWidth="100px"
                  >
                    {t('common:save')}
                  </Button>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};
export default PaychexApiSettings;
